export const MIN_LAUNCH_BUSINESS_DAYS = 5;

// Doubles as react-datepicker's filterDate (which ENABLES what it returns true for) so the selectable
// days can never disagree with the minDate derived from this same rule (PAD-32175).
export const isBusinessDay = (date: Date): boolean => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const addBusinessDays = (from: Date, businessDays: number): Date => {
  const result = new Date(from);
  let added = 0;
  while (added < businessDays) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay(result)) {
      added += 1;
    }
  }
  return result;
};

const parseDate = (value: string | number | Date): Date => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      return new Date(Number(trimmed));
    }
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
    return new Date(trimmed);
  }
  return new Date(value);
};

const atMidnight = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const isLaunchDateOutdated = (
  startDate: string | number | Date | null | undefined,
  minBusinessDays: number = MIN_LAUNCH_BUSINESS_DAYS,
  now: Date = new Date()
): boolean => {
  if (startDate === null || startDate === undefined || startDate === "") {
    return false;
  }
  const start = parseDate(startDate);
  if (Number.isNaN(start.getTime())) {
    return false;
  }
  return atMidnight(start) < atMidnight(addBusinessDays(now, minBusinessDays));
};

export const getEarliestLaunchDate = (
  minBusinessDays: number = MIN_LAUNCH_BUSINESS_DAYS,
  now: Date = new Date()
): Date => atMidnight(addBusinessDays(now, minBusinessDays));

/**
 * Serialize a picked launch date for persistence: UTC midnight of the user's
 * chosen calendar day (e.g. "2026-06-19T00:00:00.000Z"), independent of the
 * viewer's timezone. Mirrors the legacy `moment(date).format("YYYY-MM-DD") +
 * "T00:00:00.000Z"`. Plain `date.toISOString()` must NOT be used: it shifts the
 * day back in positive-offset timezones (e.g. someone in Australia editing a US
 * campaign would save the previous day). Read-side normalization back to the
 * viewer's local midnight is handled by CampaignDataContext on hydration.
 */
export const toPersistedLaunchDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00.000Z`;
};
